import { useEffect, useRef, useState } from 'react';
import type { MapSummary } from '@orbit-mind/offline-engine';
import { localStorageLayer } from '../engine/storage';
import { templatePreviewSrc } from './templatePreview';

/** Only render previews near the viewport; a large library should not arrange every map. */
export function MapThumbnail({ summary }: { readonly summary: MapSummary }): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let loaded = false;
    const load = async (): Promise<void> => {
      if (loaded) return;
      loaded = true;
      try {
        const checkpoint = await localStorageLayer().journal.readCheckpoint(summary.map.id);
        if (!cancelled && checkpoint !== undefined)
          setSrc(templatePreviewSrc(checkpoint.document, 'Inter Variable, sans-serif'));
      } catch {
        /* The title still opens the map and its recovery flow. */
      }
    };
    if (typeof IntersectionObserver === 'undefined') {
      void load();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void load();
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );
    if (ref.current !== null) observer.observe(ref.current);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [summary.map.id, summary.map.updatedAt]);
  return (
    <div
      ref={ref}
      className="bg-surface-sunken/50 flex h-36 w-full items-center justify-center overflow-hidden rounded-xl p-4"
      aria-hidden="true"
    >
      {src === null ? (
        <span className="text-accent/30 text-5xl">◎</span>
      ) : (
        <img src={src} alt="" loading="lazy" className="h-full w-full object-contain" />
      )}
    </div>
  );
}
